import { WallPathShape, WallShape } from "../types";
import { DEFAULT_WALL_COLOR, DEFAULT_WALL_THICKNESS } from "../components/map/defines";

/**
 * Decompose a WallPathShape into virtual WallShape objects (one per edge).
 * This lets existing utilities (wallJunction, wallFloorFill, wallBridge, snap)
 * work without major rewrites — they just see individual wall segments.
 */
export const wallPathToSegments = (path: WallPathShape): WallShape[] => {
  const { vertices, edges, closed } = path;
  if (vertices.length < 2) return [];

  const edgeCount = closed ? vertices.length : vertices.length - 1;
  const segments: WallShape[] = [];

  for (let i = 0; i < edgeCount; i++) {
    const from = vertices[i];
    const to = vertices[(i + 1) % vertices.length];
    const edge = edges[i] || {};

    segments.push({
      id: `${path.id}-edge-${i}`,
      type: "wall",
      x: from.x,
      y: from.y,
      x2: to.x,
      y2: to.y,
      width: 0,
      height: 0,
      thickness: path.thickness || DEFAULT_WALL_THICKNESS,
      color: path.color || DEFAULT_WALL_COLOR,
      layerType: path.layerType,
      doors: edge.doors,
      windows: edge.windows,
      curvature: edge.curvature,
      curvatureCenter: edge.curvatureCenter,
      excludeFromFloorFill: path.excludeFromFloorFill
    });
  }

  return segments;
};

/**
 * Extract all WallShape segments from a mixed shapes array.
 * Returns real WallShape items plus virtual segments from WallPathShape items.
 */
export const getAllWallSegments = (shapes: { type: string; hidden?: boolean }[]): WallShape[] => {
  const walls: WallShape[] = [];

  for (const shape of shapes) {
    if (shape.hidden) continue;
    if (shape.type === "wall") {
      walls.push(shape as WallShape);
    } else if (shape.type === "wall-path") {
      walls.push(...wallPathToSegments(shape as WallPathShape));
    }
  }

  return walls;
};
